import { Globe, Check, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useRegion } from "../context/RegionContext";
import { currencySymbol } from "../lib/commercial";
import i18n, { countryName } from "../i18n";

// Traduction (Mon espace) : les textes suivent la langue active ; la page parente s'abonne aux changements via useTranslation.
const tr = (k, o) => i18n.t(k, o);

const currencyLabel = (c) => currencySymbol(c);

// Sélecteur global de région (header). Structuré en sections de menu afin de pouvoir
// accueillir ultérieurement une section « Langue » SANS refonte du header.
// À ce stade (B2.1) il ne gère QUE la région ; la langue restera indépendante
// (futur LocaleContext distinct — ne jamais déduire la langue de la région).
// Région mono-pays : nom du pays localisé (Intl) ; région multi-pays : libellé configuré.
const regionName = (r) => ((r.country_codes || []).length <= 1 && r.country_code ? countryName(r.country_code) : r.name);

export const RegionSelector = ({ variant = "desktop" }) => {
  const { regions, region, selectRegion, ready } = useRegion();
  if (!ready || !regions || regions.length < 2) return null;

  const activeRegion = regions.find((r) => r.code === region);
  const isMobile = variant === "mobile";
  const triggerClass = isMobile
    ? "w-full justify-between border-slate-200 bg-white hover:bg-slate-100 text-slate-900"
    : "border-slate-200 bg-white hover:bg-slate-100 text-slate-900 px-2.5";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          data-testid={isMobile ? "region-selector-btn-mobile" : "region-selector-btn"}
          variant="outline"
          size={isMobile ? "default" : "icon"}
          className={triggerClass}
          aria-label={activeRegion ? regionName(activeRegion) : tr("account:region.region")}
          title={activeRegion ? regionName(activeRegion) : tr("account:region.region")}
        >
          <Globe className={`w-4 h-4 shrink-0 text-emerald-600 ${isMobile ? "mr-2" : ""}`} />
          {isMobile && (
            <>
              <span className="truncate">{activeRegion ? regionName(activeRegion) : tr("account:region.region")}</span>
              <ChevronDown className="w-4 h-4 ml-2 shrink-0 text-slate-400" />
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60" data-testid="region-selector-menu">
        <DropdownMenuLabel>{tr("account:region.region")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {regions.map((r) => (
          <DropdownMenuItem
            key={r.code}
            data-testid={`region-option-${r.code}`}
            onClick={() => selectRegion(r.code)}
            className="cursor-pointer"
          >
            <div className="flex items-center justify-between w-full gap-2">
              <span className="truncate text-sm">
                {regionName(r)} ({currencyLabel(r.currency)})
              </span>
              {region === r.code && <Check className="w-4 h-4 text-emerald-600 shrink-0" />}
            </div>
          </DropdownMenuItem>
        ))}
        {/*
          FUTUR i18n — ajouter ici une section « Langue » :
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Langue</DropdownMenuLabel>
            ...items...
          Elle s'appuiera sur un LocaleContext distinct (region et langue indépendants).
        */}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
